import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { NULL_TENANT_ID, isCustomerUser, tenantClaimsOf, tenantIdOf } from '@/integrations/supabase/session';
import { useAuth } from '@/hooks/useAuth';
import type { Company } from '@/types';
import { isPermission, type Permission } from '@/lib/permissions';
import {
  COMPANY_SELECT_COLUMNS,
  TenantContext,
  type AppRole,
  type TenantContextValue,
  type TenantMe,
} from './tenantContext';

const NO_ROLES: AppRole[] = [];
const NO_PERMISSIONS: Permission[] = [];

async function fetchMe(userId: string): Promise<TenantMe> {
  const [rolesRes, permsRes, superRes, profileRes] = await Promise.all([
    supabase.from('user_roles').select('role').eq('user_id', userId),
    supabase.from('user_permissions').select('permission').eq('user_id', userId),
    supabase.from('super_admins').select('id').eq('user_id', userId).maybeSingle(),
    supabase.from('profiles').select('full_name, email, company_id').eq('id', userId).maybeSingle(),
  ]);

  if (rolesRes.error) throw rolesRes.error;
  // A permissions failure must not lock the user out: fall back to role-only visibility.
  if (permsRes.error) console.warn('user_permissions lookup failed; using role-only visibility', permsRes.error);

  return {
    roles: (rolesRes.data ?? []).map((r) => r.role as AppRole),
    permissions: (permsRes.data ?? []).map((p) => p.permission).filter(isPermission),
    isSuperAdmin: !superRes.error && !!superRes.data,
    fullName: profileRes.data?.full_name ?? null,
    email: profileRes.data?.email ?? null,
    profileCompanyId: profileRes.data?.company_id ?? null,
  };
}

async function fetchCompany(tenantId: string): Promise<Company> {
  const { data, error } = await supabase
    .from('companies')
    .select(COMPANY_SELECT_COLUMNS)
    .eq('id', tenantId)
    .single();
  if (error) throw error;
  return data as Company;
}

/**
 * Single source of tenant identity for the session. Identity comes from the
 * JWT claims (free); the two queries behind it are fetched once per session
 * and refreshed only by explicit invalidation (settings save, realtime
 * broadcast on companies/profiles/user_roles, or a tenant_id claim change).
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const claims = tenantClaimsOf(user);
  const claimedTenantId = tenantIdOf(user);

  const meQuery = useQuery({
    queryKey: ['tenant', 'me', userId],
    queryFn: () => fetchMe(userId!),
    // Customer-portal logins have no profile/roles; skip the lookup so
    // realtime, timezone and softphone behave as for an anonymous visitor.
    enabled: !!userId && !isCustomerUser(user),
    staleTime: Infinity,
  });

  const me = meQuery.data;
  const profileTenantId = me?.profileCompanyId && me.profileCompanyId !== NULL_TENANT_ID
    ? me.profileCompanyId
    : undefined;
  const tenantId = claimedTenantId ?? profileTenantId;

  const companyQuery = useQuery({
    queryKey: ['tenant', tenantId, 'company'],
    queryFn: () => fetchCompany(tenantId!),
    enabled: !!tenantId,
    staleTime: Infinity,
  });

  const roles = me?.roles ?? NO_ROLES;
  const permissions = me?.permissions ?? NO_PERMISSIONS;
  const company = companyQuery.data;
  const isLoadingMe = meQuery.isLoading;
  const isLoadingCompany = companyQuery.isLoading;

  const value = useMemo<TenantContextValue>(() => ({
    userId,
    tenantId,
    tenantClaim: claims.tenant_id,
    tenantSlug: claims.tenant_slug,
    role: claims.role,
    isAssumed: claims.is_assumed,
    hasTenant: !!tenantId,
    me,
    roles,
    isAdmin: roles.includes('admin'),
    isCSR: roles.includes('csr'),
    isAffiliate: roles.includes('affiliate'),
    isTech: roles.includes('tech'),
    isTechOnly: roles.includes('tech') && !roles.some((r) => r === 'admin' || r === 'csr' || r === 'affiliate'),
    isSuperAdmin: me?.isSuperAdmin ?? false,
    hasRole: (role: AppRole) => roles.includes(role),
    permissions,
    hasPermission: (permission: Permission) =>
      roles.includes('admin') || (me?.isSuperAdmin ?? false) || permissions.includes(permission),
    company,
    isLoadingMe,
    isLoadingCompany,
    isLoading: isLoadingMe || isLoadingCompany,
    companyQueryKey: ['tenant', tenantId, 'company'] as const,
  }), [userId, tenantId, claims.tenant_id, claims.tenant_slug, claims.role, claims.is_assumed, me, roles, permissions, company, isLoadingMe, isLoadingCompany]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
